import { useCallback, useEffect, useMemo } from "react";

import { Form, Select, Skeleton, Typography } from "antd";
import { clsx } from "clsx";

import { RVButton, RVDescriptionSelectOption, RVFormItem } from "@regal-voice/ui";
import selectStyles from "@regal-voice/ui/components/molecules/RVDescriptionSelectOption/RVDescriptionSelectOption.module.scss";
import { RECORDING_TYPE_OPTIONS } from "Pages/settings/Pages/Recordings/constants";

import type { PrerecordedVoiceMessage } from "Types/Recordings";

import styles from "./VoicemailListForm.module.scss";

export interface VoicemailListFormProps {
    campaignId?: string;
    loading: boolean;
    setVmRecordingId: (value: string) => void;
    isDroppingVoicemail?: boolean;
    dropVoicemail: () => Promise<void>;
    voiceMailOptionData: Array<PrerecordedVoiceMessage>;
}

const DEFAULT_DESC = "Account Default VM";
const DEFAULT_AGENT_DESC = "Agent Default VM";
const CAMPAIGN_DESC = "Campaign VM";

export const sortVoicemails = (a: PrerecordedVoiceMessage, b: PrerecordedVoiceMessage) => {
    const leftName = a.friendlyName;
    const rightName = b.friendlyName;
    if (leftName.toLowerCase() > rightName.toLowerCase()) {
        return 1;
    }

    if (leftName.toLowerCase() < rightName.toLowerCase()) {
        return -1;
    }
    return 0;
};

export function VoicemailListForm({
    campaignId,
    voiceMailOptionData,
    setVmRecordingId,
    dropVoicemail,
    loading,
    isDroppingVoicemail,
}: VoicemailListFormProps) {
    const [form] = Form.useForm();
    const options = useMemo(() => {
        const [defaultVoicemail, ...voicemails] = voiceMailOptionData;
        // sort alphabetically
        const sortedData = [defaultVoicemail, ...voicemails.sort(sortVoicemails)];

        return sortedData?.map((voicemail) => {
            let description = "";

            if (voicemail.isDefault) {
                if (voicemail.type === RECORDING_TYPE_OPTIONS.VOICEMAIL) {
                    description = DEFAULT_DESC;
                } else if (voicemail.type === RECORDING_TYPE_OPTIONS.AGENT_VOICEMAIL) {
                    description = DEFAULT_AGENT_DESC;
                }
            } else {
                voicemail?.phoneCallCampaigns?.forEach((pcCamp) => {
                    // there can be alot of agent vms associate with a campaign so we only want to show the brand vm for the campaign
                    if (pcCamp?.campaign?.id === campaignId && voicemail.type === RECORDING_TYPE_OPTIONS.VOICEMAIL) {
                        description = CAMPAIGN_DESC;
                    }
                });
            }
            return {
                name: voicemail.friendlyName,
                value: voicemail.id,
                label: <RVDescriptionSelectOption name={voicemail.friendlyName} description={description} />,
            };
        });
    }, [campaignId, voiceMailOptionData]);

    useEffect(() => {
        if (options?.[0]?.value) {
            setVmRecordingId(options[0].value);
        }
    }, [options, setVmRecordingId]);

    const handleVMSelect = useCallback(
        (value: string) => {
            setVmRecordingId(value);
        },
        [setVmRecordingId]
    );

    if (loading) {
        return (
            <div data-testid="voicemail-list-loading" className={styles.container}>
                <Skeleton active paragraph={{ rows: 1, width: "100%" }} />
            </div>
        );
    }

    return (
        <div className={styles.container} data-testid="voicemail-list">
            <Typography.Title level={5} className={styles.title}>
                Select Voicemail
            </Typography.Title>
            <Form
                form={form}
                initialValues={{
                    type: options?.[0]?.value,
                }}
            >
                <RVFormItem name="type" className={styles.formItem}>
                    <Select
                        showSearch
                        className={selectStyles.forceSelectFix}
                        popupClassName={selectStyles.forceSelectDropFix}
                        options={options}
                        onChange={handleVMSelect}
                        optionFilterProp="name"
                    />
                </RVFormItem>
                <RVFormItem className={clsx(styles.actionButton, "flex-row justify-end")}>
                    <RVButton
                        size="middle"
                        type="primary"
                        data-testid="transfer-button"
                        onClick={dropVoicemail}
                        loading={isDroppingVoicemail}
                    >
                        Drop Voicemail
                    </RVButton>
                </RVFormItem>
            </Form>
        </div>
    );
}
